import { useMemo, useState } from 'react';

import { buildFlowQueue, type FlowStep } from './grouping';
import type { OutstandingItem } from './outstanding.schema';

/**
 * Walks the director through everything outstanding, one decision at a time.
 *
 * The queue is built **once**, when the flow opens, and never rebuilt from the
 * live query. That is deliberate: signing an item invalidates the `Document`
 * tag, the list refetches, and the item drops out of the response — so a queue
 * derived from live data would renumber itself underneath the director, turning
 * "step 2 of 4" into "step 2 of 3" mid-signature. A wizard whose length changes
 * while you are in it cannot be trusted, so the plan is frozen and progress is
 * tracked against it.
 */

export interface GuidedFlow {
  steps: FlowStep[];
  index: number;
  current: FlowStep | undefined;
  /** Steps resolved so far, by step id. Drives the progress rail and the
   *  closing summary. */
  outcomes: Map<string, 'done' | 'skipped'>;
  isComplete: boolean;
  doneCount: number;
  skippedCount: number;
  complete: () => void;
  skip: () => void;
  back: () => void;
  canGoBack: boolean;
}

export const useGuidedFlow = (items: OutstandingItem[], startAt?: string): GuidedFlow => {
  // Frozen at open: `items` is the live query result and changes under us as
  // each step succeeds. Only the initial value is ever read.
  const [steps] = useState(() => buildFlowQueue(items));

  const [index, setIndex] = useState(() => {
    if (startAt === undefined) return 0;
    const found = steps.findIndex((step) => step.item.id === startAt);
    return found === -1 ? 0 : found;
  });

  const [outcomes, setOutcomes] = useState<Map<string, 'done' | 'skipped'>>(new Map());

  const record = (outcome: 'done' | 'skipped') => {
    const step = steps[index];
    if (step === undefined) return;

    setOutcomes((previous) => {
      const next = new Map(previous);
      next.set(step.id, outcome);
      return next;
    });
    setIndex((previous) => previous + 1);
  };

  const { doneCount, skippedCount } = useMemo(() => {
    let done = 0;
    let skipped = 0;
    for (const outcome of outcomes.values()) {
      if (outcome === 'done') done += 1;
      else skipped += 1;
    }
    return { doneCount: done, skippedCount: skipped };
  }, [outcomes]);

  return {
    steps,
    index,
    current: steps[index],
    outcomes,
    isComplete: index >= steps.length,
    doneCount,
    skippedCount,
    complete: () => {
      record('done');
    },
    skip: () => {
      record('skipped');
    },
    back: () => {
      setIndex((previous) => Math.max(0, previous - 1));
    },
    canGoBack: index > 0,
  };
};
